import type { ConnectedChatSessionSnapshot } from "../generated/connectedChat";

import { reploEnvSchema } from "replo-utils/lib/env";
import { z } from "zod";

// NOTE (Yuxin, 2026-07-20): not `RequestInfo` — that type is lib.dom/workers-only and node-lib projects import this file.
export type WebhookResponderFetcher = {
  fetch(input: string | URL | Request, init?: RequestInit): Promise<Response>;
};

// NOTE (Kevin, 2026-07-22): Optional fields are nullish to match the webhook responder's nullable SQLite columns without a mapping layer.
export const webhookDeliveryStateSchema = z
  .object({
    provider: z.literal("slack"),
    messageTs: z.string().nullish(),
    // The card's one title: seeded from the user's request at prompt time,
    // overwritten whenever a newer set_status arrives.
    title: z.string().nullish(),
    // The turn's latest main-session snapshot, persisted so a subagent event
    // can re-render the card with the main turn's structure.
    mainSnapshot: z
      .custom<ConnectedChatSessionSnapshot>()
      .nullish()
      .describe("override?:ConnectedChatSessionSnapshot | null"),
    // Max tool count seen per subagent session this turn; summed with the main
    // count for a "Ran N actions" counter that never decreases.
    subagentActionCounts: z.record(z.string(), z.number()).nullish(),
    lastSeq: z.number(),
    isFinal: z.boolean().optional(),
    lastAssistantText: z.string().nullish(),
    projectName: z.string(),
    projectUrl: z.string(),
    channelId: z.string(),
    threadId: z.string().nullish(),
    nangoConnectionId: z.string(),
    externalActorId: z.string(),
    externalWorkspaceId: z.string(),
    queue: z
      .object({
        id: z.uuid(),
        itemId: z.string(),
      })
      .nullish(),
    reactionTarget: z
      .object({
        channelId: z.string(),
        timestamp: z.string(),
      })
      .nullish(),
  })
  .describe("WebhookDeliveryState");

export const connectedChatDeliveryStatePatchSchema = webhookDeliveryStateSchema
  .pick({
    mainSnapshot: true,
    messageTs: true,
    nangoConnectionId: true,
    subagentActionCounts: true,
    title: true,
  })
  .partial()
  .describe("ConnectedChatDeliveryStatePatch");

// The delivery-state slice the Slack turn renderer reads.
export const slackTurnRenderStateSchema = webhookDeliveryStateSchema
  .pick({
    channelId: true,
    externalActorId: true,
    externalWorkspaceId: true,
    mainSnapshot: true,
    projectName: true,
    projectUrl: true,
    queue: true,
    subagentActionCounts: true,
    title: true,
  })
  .describe("SlackTurnRenderState");

export const connectedChatDeliveryResultSchema = z
  .discriminatedUnion("status", [
    z.object({
      status: z.literal("delivered"),
      statePatch: connectedChatDeliveryStatePatchSchema,
    }),
    z.object({
      status: z.literal("aborted"),
      reason: z.enum(["connection_missing", "slack_auth_revoked"]),
    }),
  ])
  .describe("ConnectedChatDeliveryResult");

export type WebhookResponderDurableObjectStub = {
  fetch(input: string | URL | Request, init?: RequestInit): Promise<Response>;
};

export type WebhookResponderDurableObjectNamespace = {
  getByName(name: string): WebhookResponderDurableObjectStub;
};

const webhookResponderFetcherSchema = z
  .object({
    fetch: z.custom<WebhookResponderFetcher["fetch"]>(
      (value) => typeof value === "function",
    ),
  })
  .passthrough()
  .describe("WebhookResponderFetcher");

const webhookResponderDurableObjectNamespaceSchema = z
  .object({
    getByName: z.custom<WebhookResponderDurableObjectNamespace["getByName"]>(
      (value) => typeof value === "function",
    ),
  })
  .passthrough()
  .describe("WebhookResponderDurableObjectNamespace");

export const webhookResponderEnvSchema = z
  .object({
    AGENT_COORDINATOR_BASE_URL: z.string(),
    AGENT_COORDINATOR_SERVICE: webhookResponderFetcherSchema,
    AGENT_UI_BASE_URL: z.string(),
    BEDROCK_API: webhookResponderFetcherSchema,
    BEDROCK_INTERNAL_API_BASE_URL: z.string(),
    AGENT_COORDINATOR_SERVICE_TOKEN: z.string(),
    CLOUDFLARE_AGENT_WEBHOOK_RESPONDER_SQS_AWS_ACCESS_KEY_ID: z.string().min(1),
    CLOUDFLARE_AGENT_WEBHOOK_RESPONDER_SQS_AWS_SECRET_ACCESS_KEY: z
      .string()
      .min(1),
    CLOUDFLARE_AGENT_WEBHOOK_RESPONDER_SQS_QUEUE_URL: z.string().min(1),
    CLOUDFLARE_AGENT_WEBHOOK_RESPONDER_SQS_REGION: z.string().min(1),
    EVENTBRIDGE_CONNECTION_API_KEY: z.string().min(1),
    NANGO_SECRET_KEY: z.string(),
    NANGO_WEBHOOK_SIGNING_KEY: z.string(),
    REPLO_ENV: reploEnvSchema,
    REPLO_DEV_ALIAS: z.string().optional(),
    REPLO_INITIAL_SNAPSHOT_REF: z.string().optional(),
    SENTRY_DSN: z.string(),
    SLACK_SIGNING_SECRET: z.string(),
    // Presented by the coordinator to authenticate pushes TO this responder.
    AGENT_WEBHOOK_RESPONDER_SERVICE_TOKEN: z.string(),
    // Presented by this responder to authenticate itself TO bedrock-api.
    BEDROCK_API_SERVICE_TOKEN: z.string(),
    WEBHOOK_SESSION_COORDINATOR: webhookResponderDurableObjectNamespaceSchema,
  })
  .describe("WebhookResponderEnv");
